// src/store/slices/admin/pricingPlanSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getPricingPlans = createAsyncThunk(
    'pricingPlans/getPricingPlans',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.PRICING_PLANS, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Pricing plans retrieved', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch pricing plans', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch pricing plans';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const getPricingPlan = createAsyncThunk(
    'pricingPlans/getPricingPlan',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.PRICING_PLAN(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Pricing plan retrieved', plan: data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch pricing plan', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch pricing plan';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const createPricingPlan = createAsyncThunk(
    'pricingPlans/createPricingPlan',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.post(API_ENDPOINTS.PRICING_PLANS + '/create', data);
            const { status, message, data: responseData } = response.data;
            if (status === 201 || status === 200) {
                return { success: true, message: message || 'Pricing plan created', plan: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to create pricing plan', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to create pricing plan';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const updatePricingPlan = createAsyncThunk(
    'pricingPlans/updatePricingPlan',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.put(API_ENDPOINTS.PRICING_PLAN(id), data);
            const { status, message, data: responseData } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Pricing plan updated', plan: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to update pricing plan', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to update pricing plan';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const deletePricingPlan = createAsyncThunk(
    'pricingPlans/deletePricingPlan',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.delete(API_ENDPOINTS.PRICING_PLAN(id));
            const { status, message } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Pricing plan deleted', id };
            } else {
                return rejectWithValue({ message: message || 'Failed to delete pricing plan', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete pricing plan';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const togglePricingPlanStatus = createAsyncThunk(
    'pricingPlans/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.patch(API_ENDPOINTS.PRICING_PLAN_TOGGLE(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Status toggled', id, is_active: data?.is_active };
            } else {
                return rejectWithValue({ message: message || 'Failed to toggle status', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to toggle status';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const exportPricingPlans = createAsyncThunk(
    'pricingPlans/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.PRICING_PLANS_EXPORT, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Exported successfully', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to export', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to export';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    items: [],
    pagination: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
    currentItem: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const pricingPlanSlice = createSlice({
    name: 'pricingPlans',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearState: (state) => {
            state.items = [];
            state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.currentItem = null;
            state.isLoading = false;
            state.isSubmitting = false;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
        },
        clearCurrentItem: (state) => { state.currentItem = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPricingPlans.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPricingPlans.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload;
                const responseData = payload.data || {};
                state.items = responseData.data || [];
                state.pagination = responseData.meta || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
                state.successMessage = payload.message || 'Pricing plans retrieved';
                state.apiMessage = payload.message || 'Pricing plans retrieved';
                state.error = null;
            })
            .addCase(getPricingPlans.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch pricing plans';
                state.apiMessage = action.payload?.message || 'Failed to fetch pricing plans';
                state.successMessage = null;
                state.items = [];
                state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            })
            .addCase(getPricingPlan.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPricingPlan.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentItem = action.payload.plan || null;
                state.successMessage = action.payload.message || 'Pricing plan retrieved';
                state.apiMessage = action.payload.message || 'Pricing plan retrieved';
                state.error = null;
            })
            .addCase(getPricingPlan.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch pricing plan';
                state.apiMessage = action.payload?.message || 'Failed to fetch pricing plan';
                state.successMessage = null;
                state.currentItem = null;
            })
            .addCase(createPricingPlan.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createPricingPlan.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const newItem = action.payload.plan;
                if (newItem) {
                    state.items = [newItem, ...state.items];
                }
                state.successMessage = action.payload.message || 'Pricing plan created';
                state.apiMessage = action.payload.message || 'Pricing plan created';
                state.error = null;
            })
            .addCase(createPricingPlan.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create pricing plan';
                state.apiMessage = action.payload?.message || 'Failed to create pricing plan';
                state.successMessage = null;
            })
            .addCase(updatePricingPlan.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updatePricingPlan.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const updated = action.payload.plan;
                if (updated) {
                    state.items = state.items.map(item => item.id === updated.id ? updated : item);
                    if (state.currentItem?.id === updated.id) {
                        state.currentItem = updated;
                    }
                }
                state.successMessage = action.payload.message || 'Pricing plan updated';
                state.apiMessage = action.payload.message || 'Pricing plan updated';
                state.error = null;
            })
            .addCase(updatePricingPlan.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update pricing plan';
                state.apiMessage = action.payload?.message || 'Failed to update pricing plan';
                state.successMessage = null;
            })
            .addCase(deletePricingPlan.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deletePricingPlan.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const id = action.payload.id;
                state.items = state.items.filter(item => item.id !== id);
                if (state.currentItem?.id === id) state.currentItem = null;
                state.successMessage = action.payload.message || 'Pricing plan deleted';
                state.apiMessage = action.payload.message || 'Pricing plan deleted';
                state.error = null;
            })
            .addCase(deletePricingPlan.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete pricing plan';
                state.apiMessage = action.payload?.message || 'Failed to delete pricing plan';
                state.successMessage = null;
            })
            .addCase(togglePricingPlanStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(togglePricingPlanStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const { id, is_active } = action.payload;
                state.items = state.items.map(item => item.id === id ? { ...item, is_active } : item);
                if (state.currentItem?.id === id) state.currentItem = { ...state.currentItem, is_active };
                state.successMessage = action.payload.message || 'Status toggled';
                state.apiMessage = action.payload.message || 'Status toggled';
                state.error = null;
            })
            .addCase(togglePricingPlanStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle status';
                state.apiMessage = action.payload?.message || 'Failed to toggle status';
                state.successMessage = null;
            })
            .addCase(exportPricingPlans.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportPricingPlans.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Exported successfully';
                state.apiMessage = action.payload.message || 'Exported successfully';
                state.error = null;
            })
            .addCase(exportPricingPlans.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export';
                state.apiMessage = action.payload?.message || 'Failed to export';
                state.successMessage = null;
            });
    }
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearState,
    clearCurrentItem,
} = pricingPlanSlice.actions;

export const selectPricingPlans = (state) => state.pricingPlans?.items || [];
export const selectPricingPlanPagination = (state) => state.pricingPlans?.pagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectCurrentPricingPlan = (state) => state.pricingPlans?.currentItem || null;
export const selectPricingPlansLoading = (state) => state.pricingPlans?.isLoading || false;
export const selectPricingPlansSubmitting = (state) => state.pricingPlans?.isSubmitting || false;
export const selectPricingPlansError = (state) => state.pricingPlans?.error || null;
export const selectPricingPlansApiMessage = (state) => state.pricingPlans?.apiMessage || null;

export default pricingPlanSlice.reducer;